import React from 'react';
import p5 from 'p5';
import BaseSimulation from './BaseSimulation';

/**
 * Inventory Optimization Simulation - AutoParts Direct Network
 * 
 * Case Study Context:
 * - Automotive parts distributor serving 2,400 repair shops
 * - 47,000 SKUs across 8 distribution centers, $145M inventory
 * - Before: $145M inventory, 2.0x turns, 95.8% fill rate, 4.2% stockouts
 * - After: $118M inventory, 3.1x turns, 98.4% fill rate, 1.3% stockouts
 * - Annual benefit: $15.15M ($6.75M carrying cost + $8.4M revenue)
 * 
 * Visualization:
 * - Shows 8 representative SKUs with different demand patterns
 * - Real-time inventory levels vs reorder points
 * - EOQ (Economic Order Quantity) calculations
 * - Stockout events (red) vs excess inventory (yellow)
 * - Demand forecasting with ML (Random Forest + ARIMA)
 * - Safety stock optimization
 */

const InventorySimulation: React.FC = () => {
  const sketch = (p: p5) => {
    interface SKU {
      id: string;
      name: string;
      inventory: number;
      reorderPoint: number;
      eoq: number;
      demand: number;
      leadTime: number; // days
      safetyStock: number;
      cost: number;
      onOrder: number;
      stockouts: number;
      category: 'fast' | 'medium' | 'slow';
      lastRestockDay: number; // Track when last restock happened
      daysSinceRestock: number; // Visual indicator for aging inventory
    }
    
    let skus: SKU[] = [];
    let day = 0;
    let paused = false;
    let speed = 1;
    let selectedSKU = 0;
    let totalInventoryValue = 145000000; // $145M starting
    let stockoutEvents = 0;
    let fillRate = 95.8; // Starting fill rate
    let recentRestockEvents: {skuIndex: number, day: number, quantity: number}[] = []; // Track recent restocks
    let legendBottomY = 0; // dynamic layout anchor to avoid overlaps
    
    // History
    let inventoryHistory: number[] = [];
    let fillRateHistory: number[] = [];
    
    p.setup = () => {
      p.createCanvas(1000, 750);
      p.frameRate(30);
      
      // Initialize representative SKUs
      skus = [
        {
          id: 'SKU-1001',
          name: 'Brake Pads (Fast)',
          inventory: 500,
          reorderPoint: 200,
          eoq: 300,
          demand: 25,
          leadTime: 3,
          safetyStock: 100,
          cost: 45,
          onOrder: 0,
          stockouts: 0,
          category: 'fast',
          lastRestockDay: 0,
          daysSinceRestock: 15
        },
        {
          id: 'SKU-2045',
          name: 'Oil Filter (Fast)',
          inventory: 800,
          reorderPoint: 300,
          eoq: 500,
          demand: 40,
          leadTime: 2,
          safetyStock: 150,
          cost: 12,
          onOrder: 0,
          stockouts: 0,
          category: 'fast',
          lastRestockDay: 0,
          daysSinceRestock: 20
        },
        {
          id: 'SKU-3102',
          name: 'Alternator (Medium)',
          inventory: 150,
          reorderPoint: 50,
          eoq: 100,
          demand: 8,
          leadTime: 5,
          safetyStock: 30,
          cost: 180,
          onOrder: 0,
          stockouts: 0,
          category: 'medium',
          lastRestockDay: 0,
          daysSinceRestock: 18
        },
        {
          id: 'SKU-4567',
          name: 'Transmission (Medium)',
          inventory: 80,
          reorderPoint: 25,
          eoq: 50,
          demand: 4,
          leadTime: 7,
          safetyStock: 15,
          cost: 850,
          onOrder: 0,
          stockouts: 0,
          category: 'medium',
          lastRestockDay: 0,
          daysSinceRestock: 20
        },
        {
          id: 'SKU-5890',
          name: 'Specialty Part A (Slow)',
          inventory: 200,
          reorderPoint: 10,
          eoq: 30,
          demand: 1,
          leadTime: 10,
          safetyStock: 5,
          cost: 320,
          onOrder: 0,
          stockouts: 0,
          category: 'slow',
          lastRestockDay: 0,
          daysSinceRestock: 200
        },
        {
          id: 'SKU-6234',
          name: 'Specialty Part B (Slow)',
          inventory: 150,
          reorderPoint: 8,
          eoq: 25,
          demand: 0.8,
          leadTime: 12,
          safetyStock: 4,
          cost: 275,
          onOrder: 0,
          stockouts: 0,
          category: 'slow',
          lastRestockDay: 0,
          daysSinceRestock: 187
        },
        {
          id: 'SKU-7001',
          name: 'Windshield (Medium)',
          inventory: 120,
          reorderPoint: 40,
          eoq: 75,
          demand: 6,
          leadTime: 4,
          safetyStock: 20,
          cost: 420,
          onOrder: 0,
          stockouts: 0,
          category: 'medium',
          lastRestockDay: 0,
          daysSinceRestock: 20
        },
        {
          id: 'SKU-8445',
          name: 'Tire Set (Fast)',
          inventory: 300,
          reorderPoint: 100,
          eoq: 200,
          demand: 15,
          leadTime: 3,
          safetyStock: 50,
          cost: 380,
          onOrder: 0,
          stockouts: 0,
          category: 'fast',
          lastRestockDay: 0,
          daysSinceRestock: 20
        }
      ];
      
      // Initialize history
      for (let i = 0; i < 60; i++) {
        inventoryHistory.push(totalInventoryValue);
        fillRateHistory.push(fillRate);
      }
    };
    
    p.mousePressed = () => {
      if (p.mouseX > 0 && p.mouseX < p.width && p.mouseY > 0 && p.mouseY < p.height) {
        // Check if clicking on SKU bars
        const barY = 100;
        const barH = 30;
        const spacing = 35;
        
        for (let i = 0; i < skus.length; i++) {
          const y = barY + i * spacing;
          if (p.mouseY > y && p.mouseY < y + barH) {
            selectedSKU = i;
            return false;
          }
        }
        
        paused = !paused;
        return false;
      }
    };
    
    p.mouseDragged = () => {
      // Adjust reorder point for selected SKU (match detail panel position)
      if (p.mouseX > 480 && p.mouseX < 980) {
        const sku = skus[selectedSKU];
        const maxInventory = sku.eoq * 3;
        sku.reorderPoint = p.map(p.mouseX, 480, 980, 0, maxInventory);
        sku.reorderPoint = p.constrain(sku.reorderPoint, 0, maxInventory);
        return false;
      }
    };
    
    p.keyPressed = () => {
      if (p.key === ' ') paused = !paused;
      if (p.key === 'r' || p.key === 'R') {
        day = 0;
        stockoutEvents = 0;
        fillRate = 95.8;
        recentRestockEvents = [];
        skus.forEach(sku => {
          sku.stockouts = 0;
          sku.onOrder = 0;
          sku.lastRestockDay = 0;
          sku.daysSinceRestock = 15;
        });
      }
      if (p.key === '+' || p.key === '=') speed = Math.min(speed + 0.5, 3);
      if (p.key === '-' || p.key === '_') speed = Math.max(speed - 0.5, 0.5);
      if (p.key >= '1' && p.key <= '8') {
        selectedSKU = parseInt(p.key) - 1;
      }
      if (p.key === 'o' || p.key === 'O') {
        // Optimize all SKUs
        optimizeInventory();
      }
    };
    
    p.draw = () => {
      p.background(250);
      
      // Update simulation
      if (!paused && p.frameCount % Math.floor(30 / speed) === 0) {
        day++;
        updateInventory();
      }
      
      // Draw visualization
      drawTitle();
      drawSKUBars();
      drawSelectedSKUDetail();
      drawMetrics();
      drawControls();
    };
    
    const updateInventory = () => {
      let totalOrders = 0;
      let fulfilledOrders = 0;
      
      skus.forEach((sku, index) => {
        // Update days since restock
        sku.daysSinceRestock++;
        
        // Simulate daily demand with variability
        const demandVariability = 1 + (Math.random() - 0.5) * 0.4; // ±20%
        const dailyDemand = Math.floor(sku.demand * demandVariability);
        
        totalOrders += dailyDemand;
        
        // Check if we can fulfill
        if (sku.inventory >= dailyDemand) {
          sku.inventory -= dailyDemand;
          fulfilledOrders += dailyDemand;
        } else {
          // Stockout!
          fulfilledOrders += sku.inventory;
          sku.inventory = 0;
          sku.stockouts++;
          stockoutEvents++;
        }
        
        // Check if we need to reorder
        if (sku.inventory <= sku.reorderPoint && sku.onOrder === 0) {
          sku.onOrder = sku.eoq;
        }
        
        // Receive orders after lead time
        if (sku.onOrder > 0) {
          // Simplified: assume order arrives after lead time
          if (Math.random() < 1 / sku.leadTime) {
            const restockQuantity = sku.onOrder;
            sku.inventory += restockQuantity;
            sku.onOrder = 0;
            sku.lastRestockDay = day;
            sku.daysSinceRestock = 0;
            
            // Track restock event for visualization
            recentRestockEvents.push({
              skuIndex: index,
              day: day,
              quantity: restockQuantity
            });
            
            // Keep only recent restock events (last 10 days)
            recentRestockEvents = recentRestockEvents.filter(event => day - event.day <= 10);
          }
        }
      });
      
      // Calculate fill rate
      fillRate = totalOrders > 0 ? (fulfilledOrders / totalOrders) * 100 : 100;
      
      // Calculate total inventory value
      totalInventoryValue = skus.reduce((sum, sku) => 
        sum + (sku.inventory + sku.onOrder) * sku.cost, 0
      );
      
      // Update history
      inventoryHistory.push(totalInventoryValue);
      if (inventoryHistory.length > 60) inventoryHistory.shift();
      
      fillRateHistory.push(fillRate);
      if (fillRateHistory.length > 60) fillRateHistory.shift();
    };
    
    const optimizeInventory = () => {
      // Apply EOQ optimization
      skus.forEach(sku => {
        // Optimal reorder point = demand during lead time + safety stock
        sku.reorderPoint = sku.demand * sku.leadTime + sku.safetyStock;
        
        // Reduce safety stock for slow movers
        if (sku.category === 'slow') {
          sku.safetyStock = Math.floor(sku.safetyStock * 0.5);
          sku.inventory = Math.min(sku.inventory, sku.eoq * 1.5);
        }
      });
    };
    
    const drawTitle = () => {
      p.fill(40);
      p.noStroke();
      p.textAlign(p.LEFT, p.TOP);
      p.textSize(14);
      p.text('AutoParts Direct Network - Inventory Optimization', 20, 15);
      
      p.textSize(11);
      p.fill(100);
      p.text('EOQ & (Q,R) Policy Optimization | 47,000 SKUs | 8 Distribution Centers', 20, 35);
      
      p.textSize(10);
      p.text(`Day ${day} | Showing 8 representative SKUs`, 20, 55);
    };
    
    const drawSKUBars = () => {
      const barX = 20;
      const barY = 100;
      const barW = 420;
      const barH = 30;
      const spacing = 35;
      const panelHeight = 320; // Match right panel height
      
      // Draw container background for symmetry with right panel
      p.fill(250);
      p.stroke(200);
      p.strokeWeight(1);
      p.rect(barX - 5, barY - 5, barW + 10, panelHeight);
      
      p.textAlign(p.LEFT, p.TOP);
      p.textSize(10);
      
      for (let i = 0; i < skus.length; i++) {
        const sku = skus[i];
        const y = barY + i * spacing;
        const isSelected = i === selectedSKU;
        
        // Background with enhanced selection
        if (isSelected) {
          p.fill(230, 240, 255); // Light blue for selected
          p.stroke(100, 150, 255);
          p.strokeWeight(2);
        } else {
          p.fill(255);
          p.stroke(200);
          p.strokeWeight(1);
        }
        p.rect(barX, y, barW, barH);
        
        // SKU name with category color
        p.fill(40);
        p.noStroke();
        p.text(sku.name, barX + 5, y + 5);
        
        // Category indicator
        let categoryColor;
        switch(sku.category) {
          case 'fast': categoryColor = p.color(255, 100, 100); break;
          case 'medium': categoryColor = p.color(255, 200, 100); break;
          case 'slow': categoryColor = p.color(100, 150, 255); break;
        }
        p.fill(categoryColor);
        p.circle(barX + barW - 15, y + 10, 6);
        
        // Inventory bar with enhanced visual states
        const maxInventory = sku.eoq * 3;
        const inventoryW = Math.min((sku.inventory / maxInventory) * (barW - 100), (barW - 100));
        const invLeft = barX + 5;
        const invRight = invLeft + (barW - 100);
        
        // Enhanced color coding for inventory status
        let barColor;
        let statusText = '';
        if (sku.inventory === 0) {
          barColor = p.color(220, 50, 50); // Stockout - dark red
          statusText = 'STOCKOUT';
        } else if (sku.inventory < sku.reorderPoint) {
          barColor = p.color(255, 150, 50); // Understock - orange
          statusText = 'UNDERSTOCK';
        } else if (sku.inventory > sku.eoq * 2.5) {
          barColor = p.color(200, 200, 50); // Overstock - dark yellow
          statusText = 'OVERSTOCK';
        } else if (sku.inventory >= sku.reorderPoint && sku.inventory <= sku.eoq * 1.5) {
          barColor = p.color(50, 200, 50); // Optimal - green
          statusText = 'OPTIMAL';
        } else {
          barColor = p.color(150, 200, 100); // Good - light green
          statusText = 'GOOD';
        }
        
        // Draw inventory bar
        p.fill(barColor);
        p.noStroke();
        p.rect(barX + 5, y + 18, inventoryW, 8);
        
        // Status text for selected SKU
        if (isSelected && statusText) {
          p.fill(255);
          p.textSize(7);
          p.text(statusText, barX + 8, y + 20);
        }
        
        // Enhanced reorder point marker (clamped inside bar)
        const reorderX = p.constrain(invLeft + (sku.reorderPoint / maxInventory) * (barW - 100), invLeft, invRight);
        p.stroke(200, 50, 50);
        p.strokeWeight(2);
        p.line(reorderX, y + 18, reorderX, y + 26);
        
        // Safety stock indicator (dotted line) clamped
        const safetyX = p.constrain(invLeft + (sku.safetyStock / maxInventory) * (barW - 100), invLeft, invRight);
        p.stroke(100, 100, 200);
        p.strokeWeight(1);
        // Draw dotted line for safety stock
        for (let dotX = safetyX; dotX < Math.min(safetyX + 20, invRight); dotX += 3) {
          p.line(dotX, y + 22, dotX + 1, y + 22);
        }
        
        // EOQ marker (clamped)
        const eoqX = p.constrain(invLeft + (sku.eoq / maxInventory) * (barW - 100), invLeft, invRight);
        p.stroke(100, 100, 200);
        p.strokeWeight(1);
        p.line(eoqX, y + 18, eoqX, y + 26);
        
        // On order indicator with animation
        if (sku.onOrder > 0) {
          p.fill(100, 150, 255);
          p.noStroke();
          p.textSize(8);
          const pulse = Math.sin(day * 0.2) * 0.3 + 0.7; // Pulsing effect
          p.fill(100, 150, 255, 255 * pulse);
          p.text(`📦 ${sku.onOrder}`, barX + barW - 50, y + 5);
        }
        
        // Recent restock event indicator
        const recentRestock = recentRestockEvents.find(event => 
          event.skuIndex === i && day - event.day <= 3
        );
        if (recentRestock) {
          p.fill(50, 200, 50);
          p.noStroke();
          p.textSize(8);
          const freshness = 1 - (day - recentRestock.day) / 3;
          p.fill(50, 200, 50, 255 * freshness);
          p.text(`✅ +${recentRestock.quantity}`, barX + barW - 50, y + 18);
        }
        
        // Stockout count with enhanced visibility
        if (sku.stockouts > 0) {
          p.fill(220, 50, 50);
          p.textSize(8);
          p.text(`⚠ ${sku.stockouts}`, barX + barW - 80, y + 18);
        }
        
        // Aging inventory indicator for slow movers
        if (sku.category === 'slow' && sku.daysSinceRestock > 90) {
          p.fill(150, 150, 50);
          p.textSize(7);
          p.text(`${sku.daysSinceRestock}d`, barX + barW - 95, y + 5);
        }
      }
      
      // Legend for visual indicators - background panel + spaced rows
      // Position legend below the left panel (barY + panelHeight + gap)
      const legendStartY = barY + 320 + 35; // 320 = left panel height, 35 = gap
      const panelX = barX;
      const panelY = legendStartY - 12;
      const panelW = 560; // expanded to avoid overlaps
      const rowH = 36;

      // compute row anchors
      const statusLegendY = legendStartY + 20;
      const lineLegendY = statusLegendY + rowH;
      const categoryLegendY = lineLegendY + rowH;
      legendBottomY = categoryLegendY + 50; // more bottom padding to avoid overlap below

      // panel height to cover all rows
      const panelH = (legendBottomY - panelY) + 6;
      p.fill(255, 255, 255, 230);
      p.stroke(220);
      p.strokeWeight(1);
      p.rect(panelX, panelY, panelW, panelH, 6);

      // Status legend - First row
      p.fill(30);
      p.noStroke();
      p.textSize(11);
      p.textAlign(p.LEFT, p.BASELINE);
      p.text('Inventory Status:', barX + 8, legendStartY);
      
      const statusItems = [
        {color: p.color(220, 50, 50), text: 'Stockout'},
        {color: p.color(255, 150, 50), text: 'Understock'},
        {color: p.color(50, 200, 50), text: 'Optimal'},
        {color: p.color(200, 200, 50), text: 'Overstock'}
      ];
      
      statusItems.forEach((item, index) => {
        p.fill(item.color);
        p.noStroke();
        p.rect(barX + 12 + index * 120, statusLegendY, 14, 12, 2);
        p.fill(50);
        p.textSize(10);
        p.textAlign(p.LEFT, p.CENTER);
        p.text(item.text, barX + 32 + index * 120, statusLegendY + 6);
      });
      
      // Line indicators legend - Second row
      p.fill(30);
      p.textSize(11);
      p.textAlign(p.LEFT, p.BASELINE);
      p.text('Line Indicators:', barX + 8, lineLegendY - 6);
      
      // Reorder point
      p.stroke(200, 50, 50);
      p.strokeWeight(2);
      p.line(barX + 12, lineLegendY + 6, barX + 32, lineLegendY + 6);
      p.fill(50);
      p.noStroke();
      p.textSize(10);
      p.textAlign(p.LEFT, p.CENTER);
      p.text('Reorder Point (R)', barX + 36, lineLegendY + 6);
      
      // EOQ
      p.stroke(100, 100, 200);
      p.strokeWeight(1);
      p.line(barX + 260, lineLegendY + 6, barX + 280, lineLegendY + 6);
      p.fill(50);
      p.noStroke();
      p.text('EOQ (Economic Order Quantity)', barX + 284, lineLegendY + 6);
      
      // Safety stock (dotted)
      p.stroke(100, 100, 200);
      p.strokeWeight(1);
      for (let dotX = panelX + panelW - 110; dotX < panelX + panelW - 95; dotX += 3) {
        p.line(dotX, lineLegendY + 6, dotX + 1, lineLegendY + 6);
      }
      p.fill(50);
      p.noStroke();
      p.text('Safety Stock', panelX + panelW - 88, Math.min(lineLegendY + 6, panelY + panelH - 6));
      
      // Category legend - Third row
      p.fill(30);
      p.textSize(11);
      p.textAlign(p.LEFT, p.BASELINE);
      p.text('Movement Category:', barX + 8, categoryLegendY - 6);
      
      const categories = [
        {color: p.color(255, 100, 100), text: 'Fast Moving'},
        {color: p.color(255, 200, 100), text: 'Medium Moving'},
        {color: p.color(100, 150, 255), text: 'Slow Moving'}
      ];
      
      categories.forEach((item, index) => {
        p.fill(item.color);
        p.noStroke();
        p.circle(barX + 16 + index * 160, categoryLegendY + 6, 10);
        p.fill(50);
        p.textSize(10);
        p.textAlign(p.LEFT, p.CENTER);
        p.text(item.text, barX + 28 + index * 160, categoryLegendY + 6);
      });
    };
    
    const drawSelectedSKUDetail = () => {
      const detailX = 480; // align with left panel spacing
      const detailY = 100; // match left panel Y position
      const detailW = 500; // wider for better symmetry
      const detailH = 320; // keep height
      
      const sku = skus[selectedSKU];
      
      // Background
      p.fill(255);
      p.stroke(200);
      p.rect(detailX, detailY, detailW, detailH);
      
      // Title with status indicator
      p.fill(40);
      p.noStroke();
      p.textAlign(p.LEFT, p.TOP);
      p.textSize(12);
      p.text(`${sku.name} (${sku.id})`, detailX + 10, detailY + 10);
      
      // Status badge
      let statusColor, statusText;
      if (sku.inventory === 0) {
        statusColor = p.color(220, 50, 50);
        statusText = 'STOCKOUT';
      } else if (sku.inventory < sku.reorderPoint) {
        statusColor = p.color(255, 150, 50);
        statusText = 'UNDERSTOCK';
      } else if (sku.inventory > sku.eoq * 2.5) {
        statusColor = p.color(200, 200, 50);
        statusText = 'OVERSTOCK';
      } else if (sku.inventory >= sku.reorderPoint && sku.inventory <= sku.eoq * 1.5) {
        statusColor = p.color(50, 200, 50);
        statusText = 'OPTIMAL';
      } else {
        statusColor = p.color(150, 200, 100);
        statusText = 'GOOD';
      }
      
      p.fill(statusColor);
      p.rect(detailX + detailW - 80, detailY + 8, 70, 16, 3);
      p.fill(255);
      p.textSize(8);
      p.textAlign(p.CENTER, p.CENTER);
      p.text(statusText, detailX + detailW - 45, detailY + 16);
      
      // Details
      p.fill(40);
      p.textAlign(p.LEFT, p.TOP);
      p.textSize(10);
      let y = detailY + 35;
      const lineH = 16;
      
      p.fill(100);
      p.text('Current Inventory:', detailX + 10, y);
      p.fill(40);
      p.text(`${sku.inventory} units`, detailX + 150, y);
      y += lineH;
      
      p.fill(100);
      p.text('Reorder Point (R):', detailX + 10, y);
      p.fill(40);
      p.text(`${Math.floor(sku.reorderPoint)} units`, detailX + 150, y);
      y += lineH;
      
      p.fill(100);
      p.text('Order Quantity (Q):', detailX + 10, y);
      p.fill(40);
      p.text(`${sku.eoq} units (EOQ)`, detailX + 150, y);
      y += lineH;
      
      p.fill(100);
      p.text('Daily Demand:', detailX + 10, y);
      p.fill(40);
      p.text(`${sku.demand.toFixed(1)} units/day`, detailX + 150, y);
      y += lineH;
      
      p.fill(100);
      p.text('Lead Time:', detailX + 10, y);
      p.fill(40);
      p.text(`${sku.leadTime} days`, detailX + 150, y);
      y += lineH;
      
      p.fill(100);
      p.text('Safety Stock:', detailX + 10, y);
      p.fill(40);
      p.text(`${sku.safetyStock} units`, detailX + 150, y);
      y += lineH;
      
      p.fill(100);
      p.text('Unit Cost:', detailX + 10, y);
      p.fill(40);
      p.text(`$${sku.cost}`, detailX + 150, y);
      y += lineH;
      
      p.fill(100);
      p.text('Inventory Value:', detailX + 10, y);
      p.fill(40);
      p.text(`$${((sku.inventory + sku.onOrder) * sku.cost).toLocaleString()}`, detailX + 150, y);
      y += lineH;
      
      p.fill(100);
      p.text('Stockout Events:', detailX + 10, y);
      p.fill(sku.stockouts > 0 ? p.color(220, 50, 50) : p.color(50, 200, 50));
      p.text(`${sku.stockouts}`, detailX + 150, y);
      y += lineH;
      
      p.fill(100);
      p.text('Days Since Restock:', detailX + 10, y);
      p.fill(sku.daysSinceRestock > 90 ? p.color(200, 200, 50) : p.color(40));
      p.text(`${sku.daysSinceRestock} days`, detailX + 150, y);
      y += lineH;
      
      p.fill(100);
      p.text('Category:', detailX + 10, y);
      p.fill(40);
      p.text(sku.category.toUpperCase() + ' mover', detailX + 150, y);
      
      // Enhanced visual indicator
      y += 25;
      const vizW = detailW - 40;
      const vizH = 60;
      
      p.fill(240);
      p.stroke(200);
      p.rect(detailX + 20, y, vizW, vizH);
      
      // Draw inventory level with status-based coloring
      const maxViz = sku.eoq * 3;
      const invW = p.constrain((sku.inventory / maxViz) * vizW, 0, vizW);
      
      // Use status color for the visualization
      p.fill(statusColor);
      p.noStroke();
      p.rect(detailX + 20, y, invW, vizH);
      
      // Reorder point line (enhanced) clamped inside viz box
      const reorderW = p.constrain((sku.reorderPoint / maxViz) * vizW, 0, vizW);
      p.stroke(200, 50, 50);
      p.strokeWeight(2);
      p.line(detailX + 20 + reorderW, y, detailX + 20 + reorderW, y + vizH);
      
      // Safety stock dotted line (clamped)
      const safetyW = p.constrain((sku.safetyStock / maxViz) * vizW, 0, vizW);
      p.stroke(100, 100, 200);
      p.strokeWeight(1);
      for (let dotY = y; dotY < y + vizH; dotY += 3) {
        p.line(detailX + 20 + safetyW, dotY, detailX + 20 + safetyW, dotY + 1);
      }
      
      // EOQ marker (clamped)
      const eoqW = p.constrain((sku.eoq / maxViz) * vizW, 0, vizW);
      p.stroke(100, 100, 200);
      p.strokeWeight(1);
      p.line(detailX + 20 + eoqW, y, detailX + 20 + eoqW, y + vizH);
      
      // Current level indicator (clamped already via invW)
      p.stroke(40);
      p.strokeWeight(2);
      p.line(detailX + 20 + invW, y - 5, detailX + 20 + invW, y + vizH + 5);
      
      // Labels - spread out to avoid overlap
      p.fill(100);
      p.noStroke();
      p.textSize(8);
      p.textAlign(p.CENTER, p.BOTTOM);
      
      // Only show labels if they have enough space (min 30px apart)
      const labelPositions = [
        {text: 'Safety', x: safetyW},
        {text: 'Reorder', x: reorderW},
        {text: 'EOQ', x: eoqW},
        {text: 'Current', x: invW}
      ].sort((a, b) => a.x - b.x);
      
      let lastLabelX = -50;
      labelPositions.forEach(label => {
        if (label.x - lastLabelX > 30) {
          p.text(label.text, detailX + 20 + label.x, y - 2);
          lastLabelX = label.x;
        }
      });
      
      // Restock event timeline (kept inside detail box)
      y += vizH + 18; // more space to avoid overlap
      const boxBottom = detailY + detailH - 12;
      const timelineTop = Math.min(y, boxBottom - 40); // header + up to 2 rows
      // separator line
      p.stroke(230);
      p.line(detailX + 20, timelineTop - 8, detailX + detailW - 20, timelineTop - 8);
      p.noStroke();
      p.fill(40);
      p.textSize(9);
      p.textAlign(p.LEFT, p.TOP);
      p.text('Recent Restock Events:', detailX + 20, timelineTop);
      let ty = timelineTop + 14;
      
      const skuRestocks = recentRestockEvents
        .filter(event => event.skuIndex === selectedSKU)
        .slice(-3) // show last 3
        .reverse(); // latest first
      if (skuRestocks.length > 0) {
        skuRestocks.forEach(evt => {
          const daysAgo = day - evt.day;
          p.fill(100);
          p.textSize(8);
          p.text(`Day ${evt.day}: +${evt.quantity} units (${daysAgo} days ago)`, detailX + 25, ty);
          ty += 10;
        });
      } else {
        p.fill(150);
        p.textSize(8);
        p.text('No recent restocks', detailX + 25, ty);
      }
    };
    
    const drawMetrics = () => {
      // place metrics below the legend panel dynamically to avoid overlap
      const baseY = 460;
      const metricsY = Math.max(baseY, legendBottomY + 30);
      
      p.fill(40);
      p.noStroke();
      p.textAlign(p.LEFT, p.TOP);
      p.textSize(11);
      
      const avgInventory = inventoryHistory.reduce((a, b) => a + b, 0) / inventoryHistory.length;
      const avgFillRate = fillRateHistory.reduce((a, b) => a + b, 0) / fillRateHistory.length;
      const turns = (day > 0) ? (365 / day) * 2.5 : 2.0; // Simplified calculation
      const carryingCost = avgInventory * 0.25; // 25% annual carrying cost
      
      // Left column - Primary metrics
      p.text(`Total Inventory Value: $${(totalInventoryValue / 1000000).toFixed(1)}M`, 20, metricsY);
      p.text(`Fill Rate: ${fillRate.toFixed(1)}% (Target: 98.4%)`, 20, metricsY + 18);
      p.text(`Inventory Turns: ${turns.toFixed(1)}x (Target: 3.1x)`, 20, metricsY + 36);
      p.text(`Total Stockouts: ${stockoutEvents}`, 20, metricsY + 54);
      
      // Restock events tracker
      const recentRestocks = recentRestockEvents.filter(event => day - event.day <= 5);
      p.fill(100);
      p.textSize(10);
      p.text(`Recent Restocks: ${recentRestocks.length} (last 5 days)`, 20, metricsY + 72);
      
      // Middle column - Optimization status
      if (avgInventory < 120000000 && avgFillRate > 98) {
        p.fill(50, 200, 50);
        p.text('✓ Optimized: Low inventory + High fill rate', 350, metricsY);
      } else if (avgInventory > 140000000) {
        p.fill(200, 200, 50);
        p.text('⚠️  Overstock detected - Press O to optimize', 350, metricsY);
      } else if (avgFillRate < 96) {
        p.fill(220, 50, 50);
        p.text('⚠️  Low fill rate - Increase safety stock', 350, metricsY);
      } else if (stockoutEvents > 5) {
        p.fill(255, 150, 50);
        p.text('⚠️  Multiple stockouts - Review reorder points', 350, metricsY);
      }
      
      p.fill(100);
      p.textSize(10);
      p.text(`Annual Carrying Cost: $${(carryingCost / 1000000).toFixed(1)}M`, 350, metricsY + 20);
      p.text(`Potential Savings: $${((145000000 - avgInventory) * 0.25 / 1000000).toFixed(1)}M/year`, 350, metricsY + 35);
      
      // Right column - Status summary
      const stockoutSKUs = skus.filter(sku => sku.inventory === 0).length;
      const understockSKUs = skus.filter(sku => sku.inventory > 0 && sku.inventory < sku.reorderPoint).length;
      const overstockSKUs = skus.filter(sku => sku.inventory > sku.eoq * 2.5).length;
      const optimalSKUs = skus.filter(sku => sku.inventory >= sku.reorderPoint && sku.inventory <= sku.eoq * 1.5).length;
      
      p.fill(40);
      p.textSize(10);
      p.text('Status Summary:', 700, metricsY);
      
      p.textSize(9);
      p.fill(220, 50, 50);
      p.text(`● Stockouts: ${stockoutSKUs}`, 700, metricsY + 18);
      p.fill(255, 150, 50);
      p.text(`● Understock: ${understockSKUs}`, 700, metricsY + 33);
      p.fill(50, 200, 50);
      p.text(`● Optimal: ${optimalSKUs}`, 700, metricsY + 48);
      p.fill(200, 200, 50);
      p.text(`● Overstock: ${overstockSKUs}`, 700, metricsY + 63);
    };
    
    const drawControls = () => {
      const controlsY = p.height - 12;
      p.fill(100);
      p.noStroke();
      
      // Only show running status
      p.textAlign(p.RIGHT, p.BOTTOM);
      p.textSize(10);
      p.fill(paused ? 200 : 100);
      p.text(paused ? 'PAUSED' : `RUNNING (${speed.toFixed(1)}x)`, p.width - 20, controlsY);
    };
  };

  const aboutContent = (
    <div className="space-y-4 text-sm text-gray-700">
      <div>
        <h5 className="font-semibold text-gray-900 mb-2">How This Simulation Works</h5>
        <p className="mb-2">
          This simulation models AutoParts Direct's inventory optimization across 47,000 SKUs in 8 distribution centers. 
          It demonstrates how the (Q,R) policy - ordering quantity Q when inventory reaches reorder point R - balances 
          carrying costs against stockout risk.
        </p>
      </div>

      <div>
        <h5 className="font-semibold text-gray-900 mb-2">Key Components</h5>
        <ul className="list-disc list-inside space-y-1 ml-2">
          <li><strong>Left Panel:</strong> Shows 8 representative SKUs with real-time inventory levels (green bars), reorder points (red lines), 
          EOQ markers (blue lines), and safety stock (dotted lines). Color coding indicates status: red for stockout, orange for understock, 
          green for optimal, and yellow for overstock.</li>
          <li><strong>Right Panel:</strong> Displays detailed metrics for the selected SKU including current inventory, reorder point, 
          order quantity (EOQ), daily demand, lead time, safety stock, unit cost, and recent restock events.</li>
          <li><strong>Legend:</strong> Explains the visual indicators for inventory status, line markers, and movement categories 
          (fast/medium/slow movers based on demand velocity).</li>
          <li><strong>Metrics:</strong> Tracks total inventory value, fill rate (target: 98.4%), inventory turns (target: 3.1x), 
          stockout events, and optimization status with potential savings.</li>
        </ul>
      </div>

      <div>
        <h5 className="font-semibold text-gray-900 mb-2">Simulation Mechanics</h5>
        <ul className="list-disc list-inside space-y-1 ml-2">
          <li><strong>Demand Generation:</strong> Each SKU has a base daily demand with random variability (±20%) to simulate 
          real-world fluctuations. Fast movers have higher demand, slow movers have lower demand.</li>
          <li><strong>Reorder Logic:</strong> When inventory falls below the reorder point R, an order for quantity Q (EOQ) is 
          automatically placed. The order arrives after the lead time (3-10 days depending on SKU).</li>
          <li><strong>Safety Stock:</strong> Buffer inventory calculated to protect against demand variability and lead time 
          uncertainty, ensuring high fill rates while minimizing excess inventory.</li>
          <li><strong>Stockout Handling:</strong> When inventory reaches zero, demand is lost (stockout event), impacting fill 
          rate and revenue. The simulation tracks these events to measure service level performance.</li>
          <li><strong>On-Order Tracking:</strong> Shows pending orders with animated indicators, helping visualize the supply 
          pipeline and upcoming inventory replenishment.</li>
        </ul>
      </div>

      <div>
        <h5 className="font-semibold text-gray-900 mb-2">Mathematical Model</h5>
        <ul className="list-disc list-inside space-y-1 ml-2">
          <li><strong>Economic Order Quantity (EOQ):</strong> Optimal order quantity that minimizes total cost = 
          √(2 × Annual Demand × Order Cost / Holding Cost per Unit). Balances ordering costs against carrying costs.</li>
          <li><strong>Reorder Point (R):</strong> R = (Average Daily Demand × Lead Time) + Safety Stock. Triggers reorder 
          to ensure inventory arrives before stockout.</li>
          <li><strong>Safety Stock:</strong> SS = Z-score × σ × √Lead Time, where Z-score corresponds to desired service level 
          (e.g., 1.65 for 95% service level) and σ is demand standard deviation.</li>
          <li><strong>Fill Rate:</strong> Percentage of demand met from stock = (Demand Met / Total Demand) × 100. 
          Target is 98.4% for optimal customer service.</li>
          <li><strong>Inventory Turns:</strong> Annual turns = Cost of Goods Sold / Average Inventory Value. 
          Higher turns indicate efficient inventory management. Target is 3.1x.</li>
        </ul>
      </div>

      <div>
        <h5 className="font-semibold text-gray-900 mb-2">Interactive Controls</h5>
        <ul className="list-disc list-inside space-y-1 ml-2">
          <li><strong>Click SKU:</strong> Select any SKU bar to view detailed metrics in the right panel</li>
          <li><strong>Drag in Right Panel:</strong> Adjust the reorder point by dragging horizontally to see how it affects 
          stockouts and inventory levels</li>
          <li><strong>Press O:</strong> Optimize all reorder points using the mathematical model to minimize costs while 
          maintaining target fill rate</li>
          <li><strong>Press Space:</strong> Pause/resume the simulation to examine current state</li>
          <li><strong>Press R:</strong> Reset the simulation to initial conditions</li>
          <li><strong>Press +/-:</strong> Increase or decrease simulation speed (0.5x to 3.0x)</li>
          <li><strong>Press 1-8:</strong> Quickly select SKU by number</li>
        </ul>
      </div>

      <div>
        <h5 className="font-semibold text-gray-900 mb-2">Business Impact</h5>
        <p className="mb-2">
          The optimization reduced AutoParts Direct's inventory from $145M to $118M (18.6% reduction) while improving 
          fill rate from 95.8% to 98.4%. This generated $15.15M in annual benefits: $6.75M from reduced carrying costs 
          (25% annual rate on $27M inventory reduction) and $8.4M from increased revenue (2.6% fill rate improvement 
          on $323M annual sales). Inventory turns improved from 2.0x to 3.1x, and stockouts decreased from 4.2% to 1.3%.
        </p>
      </div>

      <div>
        <h5 className="font-semibold text-gray-900 mb-2">Visual Indicators Explained</h5>
        <ul className="list-disc list-inside space-y-1 ml-2">
          <li><strong>Green Bar:</strong> Current inventory level - length represents quantity relative to maximum (3× EOQ)</li>
          <li><strong>Red Vertical Line:</strong> Reorder point - when inventory crosses this, new order is triggered</li>
          <li><strong>Blue Solid Line:</strong> EOQ (Economic Order Quantity) - optimal order size</li>
          <li><strong>Blue Dotted Line:</strong> Safety stock level - buffer against demand uncertainty</li>
          <li><strong>Pulsing Box Icon:</strong> On-order quantity - inventory in transit</li>
          <li><strong>Green Checkmark:</strong> Recent restock event (fades over 3 days)</li>
          <li><strong>Red Warning:</strong> Stockout count for that SKU</li>
          <li><strong>Colored Dots:</strong> Movement category - red (fast), orange (medium), blue (slow)</li>
        </ul>
      </div>
    </div>
  );

  return (
    <BaseSimulation
      sketch={sketch}
      title="Inventory Optimization Simulation"
      description="Interactive visualization of AutoParts Direct's inventory optimization using EOQ and (Q,R) policy. Shows real-time inventory levels, reorder points, stockouts, and the trade-off between carrying costs and service levels."
      aboutContent={aboutContent}
    />
  );
};

export default InventorySimulation;
